import Strings from "@/shared/utils/Strings.constants";

export interface AiInsightCondition {
    title: string;
    propensity: number; // Multiplier (e.g., 2 means "2x more prone", 1.67 means "67% more likely")
    cost: number; // Average treatment cost in dollars
}

export interface BreedAiInsight {
    breedName: string;
    vetBillRange: {
        min: number;
        max: number;
    };
    conditions: AiInsightCondition[];
}

export const AI_INSIGHTS_DATA: BreedAiInsight[] = [
    {
        breedName: "French Bulldog",
        vetBillRange: { min: 4000, max: 6000 },
        conditions: [
            {
                title: "Brachycephalic Airway Syndrome (BAS)",
                propensity: 2,
                cost: 1750
            },
            {
                title: "Back/Neck Injury",
                propensity: 1.67,
                cost: 580
            },
            {
                title: "Corneal Ulcer",
                propensity: 2.7,
                cost: 491
            }
        ]
    },
    {
        breedName: "Labrador Retriever",
        vetBillRange: { min: 3000, max: 5000 },
        conditions: [
            {
                title: "Kennel Cough",
                propensity: 2,
                cost: 240
            },
            {
                title: "Ear Infection/Otitis",
                propensity: 1.66,
                cost: 443
            },
            {
                title: "Hypothyroidism",
                propensity: 2,
                cost: 295
            }
        ]
    },
    {
        breedName: "Golden Retriever",
        vetBillRange: { min: 3000, max: 5000 },
        conditions: [
            {
                title: "Foreign Object Ingestion",
                propensity: 2,
                cost: 690
            },
            {
                title: "Dermatitis",
                propensity: 1.54,
                cost: 335
            },
            {
                title: "Parasites",
                propensity: 1.41,
                cost: 210
            }
        ]
    },
    {
        breedName: "Medium Mix",
        vetBillRange: { min: 3000, max: 4000 },
        conditions: [
            {
                title: "Behavioral condition",
                propensity: 1.54,
                cost: 318
            },
            {
                title: "Fractured Toenail",
                propensity: 1.54,
                cost: 324
            },
            {
                title: "Heartworm Disease",
                propensity: 3,
                cost: 742
            }
        ]
    },
    {
        breedName: "Small Mix",
        vetBillRange: { min: 2000, max: 4000 },
        conditions: [
            {
                title: "Heart Murmur",
                propensity: 2.2,
                cost: 910
            },
            {
                title: "Patellar Luxation",
                propensity: 1.91,
                cost: 611
            },
            {
                title: "Bladder Stones",
                propensity: 2,
                cost: 3021
            }
        ]
    },
    {
        breedName: "Large Mix",
        vetBillRange: { min: 3000, max: 5000 },
        conditions: [
            {
                title: "Growth/Lump",
                propensity: 1.73,
                cost: 662
            },
            {
                title: "Cruciate Ligament Rupture/Tear",
                propensity: 2.8,
                cost: 4153
            },
            {
                title: "Collateral Ligament Rupture/Tear",
                propensity: 2.9,
                cost: 3004
            }
        ]
    },
    {
        breedName: "American Shorthair",
        vetBillRange: { min: 2000, max: 4000 },
        conditions: [
            {
                title: "Periodontal Disease",
                propensity: 1.3,
                cost: 1251
            },
            {
                title: "Ear Infection/Otitis",
                propensity: 1.36,
                cost: 364
            },
            {
                title: "Behavioral condition",
                propensity: 1.42,
                cost: 275
            }
        ]
    },
    {
        breedName: "Mixed Cat",
        vetBillRange: { min: 2000, max: 3000 },
        conditions: [
            {
                title: "Urinary tract infection",
                propensity: 1.18,
                cost: 502
            },
            {
                title: "Toxic Ingestion",
                propensity: 1.14,
                cost: 652
            },
            {
                title: "Gingivitis",
                propensity: 1.18,
                cost: 777
            }
        ]
    },
    {
        breedName: "Domestic Shorthair",
        vetBillRange: { min: 2000, max: 4000 },
        conditions: [
            {
                title: "Periodontal Disease",
                propensity: 1.22,
                cost: 1136
            },
            {
                title: "Skin Infection/Irritation",
                propensity: 1.25,
                cost: 298
            },
            {
                title: "Constipation",
                propensity: 1.25,
                cost: 730
            }
        ]
    },
    {
        breedName: "Domestic Mediumhair",
        vetBillRange: { min: 2000, max: 4000 },
        conditions: [
            {
                title: "Behavioral condition",
                propensity: 1.41,
                cost: 251
            },
            {
                title: "Upper Respiratory Infection",
                propensity: 1.3,
                cost: 331
            },
            {
                title: "Ringworm",
                propensity: 1.33,
                cost: 261
            }
        ]
    },
    {
        breedName: "Domestic Longhair",
        vetBillRange: { min: 2000, max: 4000 },
        conditions: [
            {
                title: "Periodontal Disease",
                propensity: 1.29,
                cost: 1278
            },
            {
                title: "Foreign Object Ingestion",
                propensity: 1.41,
                cost: 1722
            },
            {
                title: "Toxic Ingestion",
                propensity: 1.24,
                cost: 1236
            }
        ]
    }
];

/**
 * Get AI insights for a specific breed
 * @param breedName - The breed name to look up
 * @returns BreedAiInsight object or undefined if breed not found
 */
export const getAiInsightsByBreed = (breedName?: string): BreedAiInsight | undefined => {
    if (!breedName) return undefined;
    return AI_INSIGHTS_DATA.find(insight => breedName.includes(insight.breedName));
};

/**
 * Format a vet bill range as a display string
 * @param range - The vet bill range object
 * @returns Formatted string like "$4k-$6k"
 */
export const formatVetBillRange = (range: { min: number; max: number }): string => {
    const formatAmount = (amount: number): string => {
        if (amount >= 1000) {
            return `${Math.round(amount / 1000)}k`;
        }
        return `${amount}`;
    };

    return `${formatAmount(range.min)}-${formatAmount(range.max)}`;
};

/**
 * Format a combined vet bill range for multiple pets
 * @param ranges - Array of vet bill ranges to combine
 * @returns Formatted string like "$15k-$28k"
 */
export const formatCombinedVetBillRange = (ranges: Array<{ min: number; max: number }>): string => {
    const totalMin = ranges.reduce((sum, range) => sum + range.min, 0);
    const totalMax = ranges.reduce((sum, range) => sum + range.max, 0);

    return formatVetBillRange({ min: totalMin, max: totalMax });
};

/**
 * Format rate/propensity for AI insights (simpler than badge version)
 * @param rate - The rate multiplier
 * @returns Formatted string like "2.8x" or "67%"
 *
 * Difference from formatRateDisplay (badges):
 * - This shows ANY value >= 2 as "x" format (e.g., 2.8x, 2.9x)
 * - Badges only show whole numbers and .5 decimals as "x"
 */
export const formatRate = (rate: number): string => {
    if (rate >= 2) {
        // Show as multiplier for anything 2x or higher
        return `${rate}x`;
    } else {
        // Convert to percentage increase for values below 2
        const percentage = Math.round((rate - 1) * 100);
        return `${percentage}%`;
    }
};

/**
 * Calculate the cost with Spot coverage (10% of original cost)
 * @param originalCost - The original treatment cost
 * @returns The cost after Spot coverage (10% of original)
 */
export const calculateSpotCost = (originalCost: number): number => {
    return originalCost * 0.1;
};

/* * Get plural breed label from breed name using mappings
 * @param breedName - The breed name to look up
 * @returns The plural label if found, otherwise returns the original breed name
 */
export const getPluralBreedLabel = (breedName: string): string => {
    const mapping = Strings.PTZ_US.BREED_DISPLAY_MAPPINGS.find(m => m.breedLabel === breedName);
    return mapping?.pluralLabel || breedName;
};
